import createFetchClient from "openapi-fetch";
import createClient from "openapi-react-query";
import type { paths } from "./api-types";
import { auth } from "./firebase";
import { useEffect, useState } from "react";

// Track when the app initialized to prevent sign out during initial auth restoration
const APP_INIT_TIME = Date.now();
const AUTH_RESTORATION_WINDOW_MS = 3000; // 3 seconds grace period after page load

export const fetchClient = createFetchClient<paths>({
  baseUrl: import.meta.env.VITE_API_URL || "/",
  credentials: "include",
});

fetchClient.use({
  async onRequest({ request }) {
    // Firebase handles token caching and background refreshing.
    // getIdToken() returns the current token if valid, or refreshes it if expired.
    const user = auth.currentUser;
    if (user) {
      const token = await user.getIdToken();
      request.headers.set("Authorization", `Bearer ${token}`);
    }
    return request;
  },
  async onResponse({ response }) {
    if (response.status === 401) {
      // Don't sign out during the initial auth restoration window if Firebase
      // still thinks there's a user - this prevents race conditions on page refresh
      const timeSinceInit = Date.now() - APP_INIT_TIME;
      const isInRestorationWindow = timeSinceInit < AUTH_RESTORATION_WINDOW_MS;
      
      if (isInRestorationWindow && auth.currentUser) {
        // During initial restoration, if Firebase has a user, don't sign out
        // The retry logic in useUser will handle retrying the request
        return response;
      }
      
      // If we get a 401 outside the restoration window, it means the token is invalid
      // or the user is no longer authorized in our Prisma backend.
      auth.signOut();
    }
    return response;
  },
});

export const apiClient = createClient<paths>(fetchClient);

/**
 * Hook to fetch the current user's profile from the backend.
 * Only runs if Firebase has an authenticated user.
 */
export const useUser = () => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isAuthChecking, setIsAuthChecking] = useState(true);
  const [authReady, setAuthReady] = useState(false);

  // Sync with Firebase's internal auth state
  useEffect(() => {
    // onAuthStateChanged fires immediately if there's already a user,
    // but we need to wait a tick to ensure Firebase has fully initialized
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setIsAuthenticated(!!user);
      // Add a small delay to ensure Firebase has fully restored the session
      // before we enable queries
      setTimeout(() => {
        setIsAuthChecking(false);
        setAuthReady(true);
      }, 0);
    });

    return unsubscribe;
  }, []);

  const query = apiClient.useQuery(
    "get",
    "/api/user/me",
    {},
    {
      enabled: authReady && isAuthenticated,
      staleTime: 1000 * 60 * 5,
      retry: (failureCount, _error) => {
        // Retry once on initial load to handle race conditions during auth restoration
        if (failureCount < 1 && authReady) {
          return true;
        }
        return false;
      },
    },
  );

  return {
    ...query,
    isAuthenticated,
    // Extract user from the response envelope { user: ... }
    data: query.data?.user,
    // We are "loading" if Firebase hasn't finished checking the initial session
    // OR if the API query is still loading.
    isLoading: isAuthChecking || query.isLoading,
  };
};

/**
 * Hook to fetch users list with proper type transformation
 */
export const useUsers = () => apiClient.useQuery("get", "/api/user", {})
